import apiService from './apiService';
import authService from './authService';

/**
 * Service pour la gestion des clients (customers / marchands)
 */
class CustomerService {
  /**
   * Récupère un client spécifique
   * @param {string} id - ID du client
   */
  async getCustomer(id) {
    const response = await apiService.get(`/customers/${id}`);
    return response.data;
  }

  /**
   * Récupère le profil complet du client connecté
   * (inclut commissions[], orders[], affiliate_code, orders_count)
   * Fallback sur les données localStorage si l'API retourne 403
   */
  async getMyProfile() {
    const user = authService.getUser();
    if (!user?.id) throw new Error('Non connecté');

    try {
      const profile = await this.getCustomer(user.id);
      if (profile) {
        authService.updateLocalUser(profile);
      }
      return profile;
    } catch (err) {
      // GET /customers/{id} retourne 403 pour les clients (endpoint admin)
      // Fallback : retourner les données du localStorage (issues du login)
      if (err.status === 403 || err.message?.includes('unauthorized')) {
        console.warn('GET /customers/{id} non autorisé, fallback localStorage');
        const stored = localStorage.getItem('userData');
        if (stored) {
          return JSON.parse(stored);
        }
      }
      throw err;
    }
  }

  /**
   * Met à jour un client
   * @param {string} id - ID du client
   * @param {Object} customerData - Données conformes à CustomerRequest
   */
  async updateCustomer(id, customerData) {
    const response = await apiService.put(`/customers/${id}`, customerData);
    return response.data;
  }

  /**
   * Met à jour le profil avec une éventuelle photo (multipart si avatarFile fourni).
   * @param {string} id
   * @param {Object} fields - champs texte (les valeurs vides sont ignorées)
   * @param {File|null} avatarFile - photo de profil optionnelle
   */
  async updateProfile(id, fields = {}, avatarFile = null) {
    if (!avatarFile) {
      // Pas de fichier → mise à jour JSON classique (champs non vides uniquement)
      const clean = Object.fromEntries(
        Object.entries(fields).filter(([, v]) => v !== undefined && v !== null && v !== "")
      );
      return this.updateCustomer(id, clean);
    }

    const formData = new FormData();
    // Method spoofing : POST + _method=PUT pour envoyer un fichier vers la route update
    formData.append("_method", "PUT");
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });
    formData.append("avatar", avatarFile);

    const response = await apiService.upload(`/customers/${id}`, formData);
    return response.data;
  }
}

const customerServiceInstance = new CustomerService();
export default customerServiceInstance;
